import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Alert,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { createTooltipProps } from '../utils/fieldTooltips';
import MissingSelectOption from '../components/MissingSelectOption';
import GridPagination from '../components/GridPagination';

const parseApiBool = (value) => {
  if (value === true || value === 1 || value === '1') return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return (
      normalized === 'true' || normalized === 'y' || normalized === 'yes' || normalized === 't'
    );
  }
  return false;
};

function HireCategories() {
  const [hireCategories, setHireCategories] = useState([]);
  const [hireCategoryGroups, setHireCategoryGroups] = useState([]);
  const [hireRates, setHireRates] = useState([]);
  const [inspectionTests, setInspectionTests] = useState([]);
  const [salesNominals, setSalesNominals] = useState([]);
  const [purchaseNominals, setPurchaseNominals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingHireCategory, setEditingHireCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalError, setModalError] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingHireRateId, setEditingHireRateId] = useState(null);
  const [editingAssetTypeId, setEditingAssetTypeId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchHireCategories = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hire-categories', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setHireCategories(response.data.hireCategories);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch hire categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchHireCategoryGroups = async () => {
    try {
      const response = await axios.get('/api/hire-category-groups/dropdown');
      setHireCategoryGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch hire category groups:', error);
    }
  };

  const fetchInspectionTests = async () => {
    try {
      const response = await axios.get('/api/inspection-tests/inspection-names');
      setInspectionTests(response.data);
    } catch (error) {
      console.error('Failed to fetch inspection tests:', error);
    }
  };

  const fetchHireRates = async () => {
    try {
      const response = await axios.get('/api/hire-rates/dropdown');
      setHireRates(response.data);
    } catch (error) {
      console.error('Failed to fetch hire rates:', error);
    }
  };

  const fetchNominalCodes = async () => {
    try {
      const [salesRes, purchaseRes] = await Promise.all([
        axios.get('/api/nominal-codes', { params: { type: 'S', limit: 500 } }),
        axios.get('/api/nominal-codes', { params: { type: 'P', limit: 500 } }),
      ]);
      setSalesNominals(salesRes.data.nominalCodes || []);
      setPurchaseNominals(purchaseRes.data.nominalCodes || []);
    } catch (error) {
      console.error('Failed to fetch nominal codes:', error);
    }
  };

  useEffect(() => {
    fetchHireCategories();
    fetchHireCategoryGroups();
    fetchHireRates();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchHireCategories(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchHireCategories(newPage, searchTerm);
  };

  const handleAddHireCategory = () => {
    console.log('handleAddHireCategory called');
    console.log('Current showModal state:', showModal);
    setEditingHireCategory(null);
    setError('');
    setSuccess('');
    setModalError('');
    fetchHireCategoryGroups(); // Refresh groups when opening modal
    fetchHireRates(); // Refresh hire rates when opening modal
    fetchInspectionTests(); // Refresh inspection tests when opening modal
    fetchNominalCodes(); // Refresh nominal codes for N/C selects
    setShowModal(true);
    console.log('showModal should now be true');
  };

  const handleEditHireCategory = async (hireCategory) => {
    try {
      const response = await axios.get(`/api/hire-categories/${hireCategory.id}`);
      setEditingHireCategory(response.data);
      setModalError('');
      fetchHireCategoryGroups();
      fetchHireRates();
      fetchInspectionTests();
      fetchNominalCodes();
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch hire category:', error);
      setError('Failed to load hire category for editing');
    }
  };

  const handleSaveHireCategory = async (hireCategoryData) => {
    try {
      if (editingHireCategory) {
        const response = await axios.put(
          `/api/hire-categories/${editingHireCategory.id}`,
          hireCategoryData
        );

        // Check if there were cascade updates
        if (response.data.cascadeUpdates) {
          const { hireStockIndividual, hireStockBulk, inclusionsParentCode, inclusionsInclCat } =
            response.data.cascadeUpdates;
          const totalInclusions = (inclusionsParentCode || 0) + (inclusionsInclCat || 0);
          const totalUpdates = hireStockIndividual + hireStockBulk + totalInclusions;

          if (totalUpdates > 0) {
            let updateMessage = 'Hire category updated successfully. ';
            const updates = [];
            if (hireStockIndividual > 0) updates.push(`${hireStockIndividual} hire stock item(s)`);
            if (hireStockBulk > 0) updates.push(`${hireStockBulk} bulk hire stock item(s)`);
            if (totalInclusions > 0) updates.push(`${totalInclusions} inclusion(s)`);
            updateMessage += `Also updated ${updates.join(', ')} with the new category code.`;
            setSuccess(updateMessage);
          } else {
            setSuccess('Hire category updated successfully');
          }
        } else {
          setSuccess('Hire category updated successfully');
        }
      } else {
        await axios.post('/api/hire-categories', hireCategoryData);
        setSuccess('Hire category created successfully');
      }
      setShowModal(false);
      setEditingHireCategory(null);
      fetchHireCategories(pagination.page, searchTerm);
    } catch (error) {
      setModalError(error.response?.data?.error || 'Failed to save hire category');
    }
  };

  const handleDeleteHireCategory = async (id) => {
    if (window.confirm('Are you sure you want to delete this hire category?')) {
      try {
        await axios.delete(`/api/hire-categories/${id}`);
        setSuccess('Hire category deleted successfully');
        fetchHireCategories(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete hire category');
      }
    }
  };

  const handleDeleteAllHireCategories = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL hire categories? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your hire category records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/hire-categories');
          setSuccess(
            `All hire categories deleted successfully (${response.data.deletedCount} records)`
          );
          fetchHireCategories(1, '');
        } catch (error) {
          setError('Failed to delete all hire categories');
        }
      }
    }
  };

  const handleGroupChange = async (hireCategoryId, newGroupName) => {
    try {
      const hireCategory = hireCategories.find((hc) => hc.id === hireCategoryId);
      if (!hireCategory) return;

      await axios.put(`/api/hire-categories/${hireCategoryId}`, {
        ...hireCategory,
        group_name: newGroupName,
      });

      setSuccess('Group updated successfully');
      setEditingGroupId(null);
      setHireCategories((prev) =>
        prev.map((hc) => (hc.id === hireCategoryId ? { ...hc, group_name: newGroupName } : hc))
      );
    } catch (error) {
      setError('Failed to update group');
      setEditingGroupId(null);
    }
  };

  const handleHireRateChange = async (hireCategoryId, newHireRateId) => {
    try {
      const hireCategory = hireCategories.find((hc) => hc.id === hireCategoryId);
      if (!hireCategory) return;

      const response = await axios.put(`/api/hire-categories/${hireCategoryId}`, {
        ...hireCategory,
        hire_rate_id: newHireRateId ? parseInt(newHireRateId, 10) : null,
      });

      let successMsg = 'Hire rate updated successfully';
      if (response.data?.rateCascadeUpdates) {
        const { hireStockIndividual, hireStockBulk } = response.data.rateCascadeUpdates;
        const total = (hireStockIndividual || 0) + (hireStockBulk || 0);
        if (total > 0) {
          const parts = [];
          if (hireStockIndividual > 0) parts.push(`${hireStockIndividual} hire stock`);
          if (hireStockBulk > 0) parts.push(`${hireStockBulk} bulk hire stock`);
          successMsg += `. Rates applied to ${parts.join(' and ')} item(s).`;
        }
      }
      setSuccess(successMsg);
      setEditingHireRateId(null);
      const parsedRateId = newHireRateId ? parseInt(newHireRateId, 10) : null;
      setHireCategories((prev) =>
        prev.map((hc) => (hc.id === hireCategoryId ? { ...hc, hire_rate_id: parsedRateId } : hc))
      );
    } catch (error) {
      setError('Failed to update hire rate');
      setEditingHireRateId(null);
    }
  };

  const handleAssetTypeChange = async (hireCategoryId, newAssetType) => {
    try {
      const hireCategory = hireCategories.find((hc) => hc.id === hireCategoryId);
      if (!hireCategory) return;

      await axios.put(`/api/hire-categories/${hireCategoryId}`, {
        ...hireCategory,
        asset_type: newAssetType || '',
      });

      setSuccess('Asset type updated successfully');
      setEditingAssetTypeId(null);
      setHireCategories((prev) =>
        prev.map((hc) =>
          hc.id === hireCategoryId ? { ...hc, asset_type: newAssetType || '' } : hc
        )
      );
    } catch (error) {
      setError('Failed to update asset type');
      setEditingAssetTypeId(null);
    }
  };

  console.log('HireCategories component rendering, showModal:', showModal);

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-tags me-3 text-success" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">Hire Categories</h1>
              <p className="text-muted mb-0">
                Manage rental equipment categories and their properties
              </p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button variant="primary" onClick={handleAddHireCategory} className="me-2">
            Add Hire Category
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllHireCategories}
            disabled={hireCategories.length === 0}
          >
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <div className="card">
        <div className="card-header">
          <form onSubmit={handleSearch} className="d-flex gap-2">
            <input
              type="text"
              placeholder="Search by code, name, or group..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
            <button type="submit" className="btn btn-primary btn-sm">
              <i className="bi bi-search me-1"></i>
              Search
            </button>
          </form>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Loading hire categories...
            </div>
          ) : hireCategories.length === 0 ? (
            <div className="empty-state">
              <i className="bi bi-collection"></i>
              <p className="mb-0">No hire categories found</p>
            </div>
          ) : (
            <div className="table-responsive" style={{ paddingLeft: '1rem' }}>
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th className="border-0">Code</th>
                    <th className="border-0">Name</th>
                    <th className="border-0">Min Days</th>
                    <th className="border-0">Group</th>
                    <th className="border-0">Hire Rate</th>
                    <th className="border-0">Asset Type</th>
                    <th className="border-0">Options</th>
                    <th className="border-0">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {hireCategories.map((hireCategory) => (
                    <tr key={hireCategory.id}>
                      <td>
                        <strong>{hireCategory.code}</strong>
                      </td>
                      <td>{hireCategory.name}</td>
                      <td>{hireCategory.min_days}</td>
                      <td
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (editingGroupId !== hireCategory.id) {
                            setEditingGroupId(hireCategory.id);
                            fetchHireCategoryGroups();
                          }
                        }}
                      >
                        {editingGroupId === hireCategory.id ? (
                          <Form.Select
                            value={hireCategory.group_name || ''}
                            onChange={(e) => handleGroupChange(hireCategory.id, e.target.value)}
                            onBlur={() => setEditingGroupId(null)}
                            autoFocus
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">Select a group...</option>
                            <MissingSelectOption
                              value={hireCategory.group_name}
                              options={hireCategoryGroups}
                              getOptionValue={(group) => group.group_name}
                            />
                            {hireCategoryGroups.map((group) => (
                              <option key={group.id} value={group.group_name}>
                                {group.group_name}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <span>
                            {hireCategory.group_name || (
                              <em style={{ color: '#999' }}>Click to set</em>
                            )}
                          </span>
                        )}
                      </td>
                      <td
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (editingHireRateId !== hireCategory.id) {
                            setEditingHireRateId(hireCategory.id);
                            fetchHireRates();
                          }
                        }}
                      >
                        {editingHireRateId === hireCategory.id ? (
                          <Form.Select
                            value={hireCategory.hire_rate_id || ''}
                            onChange={(e) => handleHireRateChange(hireCategory.id, e.target.value)}
                            onBlur={() => setEditingHireRateId(null)}
                            autoFocus
                            size="sm"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">Select a hire rate...</option>
                            <MissingSelectOption
                              value={hireCategory.hire_rate_id}
                              options={hireRates}
                              getOptionValue={(rate) => String(rate.id)}
                            />
                            {hireRates.map((rate) => (
                              <option key={rate.id} value={rate.id}>
                                {rate.name}
                              </option>
                            ))}
                          </Form.Select>
                        ) : (
                          <span>
                            {hireCategory.hire_rate_id ? (
                              hireRates.find((r) => r.id === hireCategory.hire_rate_id)?.name || (
                                <em style={{ color: '#999' }}>—</em>
                              )
                            ) : (
                              <em style={{ color: '#999' }}>Click to set</em>
                            )}
                          </span>
                        )}
                      </td>
                      <td
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (editingAssetTypeId !== hireCategory.id) {
                            setEditingAssetTypeId(hireCategory.id);
                          }
                        }}
                      >
                        {editingAssetTypeId === hireCategory.id ? (
                          <Form.Control
                            type="text"
                            maxLength={50}
                            size="sm"
                            defaultValue={hireCategory.asset_type || ''}
                            onBlur={(e) => {
                              handleAssetTypeChange(hireCategory.id, e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.target.blur();
                              }
                            }}
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span>
                            {hireCategory.asset_type || (
                              <em style={{ color: '#999' }}>Click to set</em>
                            )}
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {/* Helper function to check if a value is truly enabled */}
                          {(() => {
                            const options = [];

                            if (parseApiBool(hireCategory.req_serial_no)) {
                              options.push(
                                <span
                                  key="serial"
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                  }}
                                >
                                  Serial
                                </span>
                              );
                            }

                            if (parseApiBool(hireCategory.is_transport)) {
                              options.push(
                                <span
                                  key="transport"
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    backgroundColor: '#28a745',
                                    color: 'white',
                                  }}
                                >
                                  Transport
                                </span>
                              );
                            }

                            if (parseApiBool(hireCategory.meter_reading)) {
                              options.push(
                                <span
                                  key="meter"
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    backgroundColor: '#ffc107',
                                    color: 'black',
                                  }}
                                >
                                  Meter
                                </span>
                              );
                            }

                            // Add other potential boolean options that might be showing as false/0
                            if (parseApiBool(hireCategory.warn_no_transport)) {
                              options.push(
                                <span
                                  key="warn"
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                  }}
                                >
                                  Warn
                                </span>
                              );
                            }

                            if (parseApiBool(hireCategory.dont_suspend)) {
                              options.push(
                                <span
                                  key="nosuspend"
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    backgroundColor: '#6f42c1',
                                    color: 'white',
                                  }}
                                >
                                  No Suspend
                                </span>
                              );
                            }

                            if (parseApiBool(hireCategory.dont_discount)) {
                              options.push(
                                <span
                                  key="nodiscount"
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    backgroundColor: '#fd7e14',
                                    color: 'white',
                                  }}
                                >
                                  No Discount
                                </span>
                              );
                            }

                            if (parseApiBool(hireCategory.asset_only)) {
                              options.push(
                                <span
                                  key="assetonly"
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontSize: '10px',
                                    backgroundColor: '#20c997',
                                    color: 'white',
                                  }}
                                >
                                  Asset Only
                                </span>
                              );
                            }

                            return options;
                          })()}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditHireCategory(hireCategory)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteHireCategory(hireCategory.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && hireCategories.length > 0 && (
          <div className="card-footer">
            <GridPagination
              page={pagination.page}
              pages={pagination.pages}
              onPageChange={handlePageChange}
              size="sm"
              ulClassName="mb-0 justify-content-center"
              wrapperClassName=""
              aria-label="Hire categories pagination"
              prevLabel={<i className="bi bi-chevron-left"></i>}
              nextLabel={<i className="bi bi-chevron-right"></i>}
            />
          </div>
        )}
      </div>

      {showModal && (
        <Modal
          show={true}
          onHide={() => {
            setShowModal(false);
            setEditingHireCategory(null);
            setModalError('');
          }}
          size="lg"
          centered
          style={{ zIndex: 1060 }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {editingHireCategory ? 'Edit Hire Category' : 'Add New Hire Category'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalError && (
              <Alert variant="danger" dismissible onClose={() => setModalError('')}>
                {modalError}
              </Alert>
            )}
            <Form
              key={editingHireCategory?.id ?? 'new'}
              id="hireCategoryForm"
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const data = {
                  code: formData.get('code'),
                  name: formData.get('name'),
                  min_days: formData.get('min_days') || '',
                  depreciation_rate: formData.get('depreciation_rate') || '',
                  asset_type: formData.get('asset_type') || '',
                  group_name: formData.get('group_name') || '',
                  hire_rate_id: formData.get('hire_rate_id') || null,
                  workshop_group: formData.get('workshop_group') || '',
                  inspection_test: formData.get('inspection_test') || '',
                  pre_delivery_inspection: formData.get('pre_delivery_inspection') || '',
                  nc_hire: formData.get('nc_hire') || '',
                  nc_operated: formData.get('nc_operated') || '',
                  nc_sales: formData.get('nc_sales') || '',
                  nc_purchase: formData.get('nc_purchase') || '',
                  req_serial_no: formData.get('req_serial_no') === 'on',
                  warn_no_transport: formData.get('warn_no_transport') === 'on',
                  is_transport: formData.get('is_transport') === 'on',
                  dont_suspend: formData.get('dont_suspend') === 'on',
                  dont_discount: formData.get('dont_discount') === 'on',
                  asset_only: formData.get('asset_only') === 'on',
                  meter_reading: formData.get('meter_reading') === 'on',
                };
                handleSaveHireCategory(data);
              }}
            >
              <Tabs defaultActiveKey="details" className="mb-3">
                <Tab eventKey="details" title="Details">
                  <Container fluid>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Code *</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('code')}>
                                Used in equipment classification, pricing, and hire contract
                                management
                              </Tooltip>
                            }
                          >
                            <Form.Control
                              type="text"
                              name="code"
                              required
                              maxLength="20"
                              defaultValue={editingHireCategory?.code || ''}
                              {...createTooltipProps('code')}
                            />
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Name *</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('name')}>
                                Used in user interfaces, reports, and equipment selection
                              </Tooltip>
                            }
                          >
                            <Form.Control
                              type="text"
                              name="name"
                              required
                              maxLength="100"
                              defaultValue={editingHireCategory?.name || ''}
                              {...createTooltipProps('name')}
                            />
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Min Days</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('min_days')}>
                                Minimum hire period for this category
                              </Tooltip>
                            }
                          >
                            <Form.Control
                              type="number"
                              name="min_days"
                              min="0"
                              defaultValue={editingHireCategory?.min_days || ''}
                              {...createTooltipProps('min_days')}
                            />
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Depreciation Rate</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('depreciation_rate')}>
                                Rate used for asset depreciation calculations
                              </Tooltip>
                            }
                          >
                            <Form.Control
                              type="text"
                              name="depreciation_rate"
                              maxLength="20"
                              defaultValue={editingHireCategory?.depreciation_rate || ''}
                              {...createTooltipProps('depreciation_rate')}
                            />
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Asset Type</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('asset_type')}>
                                Type of asset for accounting and reporting purposes
                              </Tooltip>
                            }
                          >
                            <Form.Control
                              type="text"
                              name="asset_type"
                              maxLength="50"
                              defaultValue={editingHireCategory?.asset_type || ''}
                              {...createTooltipProps('asset_type')}
                            />
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Group Name</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('group_name')}>
                                Provides hierarchical organization of hire categories
                              </Tooltip>
                            }
                          >
                            <Form.Select
                              name="group_name"
                              // Remount when options arrive: uncontrolled defaultValue only
                              // applies at first render, before async lists have loaded
                              key={`grp-${editingHireCategory?.id || 'new'}-${hireCategoryGroups.length}`}
                              defaultValue={editingHireCategory?.group_name || ''}
                              {...createTooltipProps('group_name')}
                            >
                              <option value="">Select a group...</option>
                              <MissingSelectOption
                                value={editingHireCategory?.group_name}
                                options={hireCategoryGroups}
                                getOptionValue={(group) => group.group_name}
                              />
                              {hireCategoryGroups.map((group) => (
                                <option key={group.id} value={group.group_name}>
                                  {group.group_name}
                                </option>
                              ))}
                            </Form.Select>
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Workshop Group</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('workshop_group')}>
                                Workshop group assignment for maintenance
                              </Tooltip>
                            }
                          >
                            <Form.Control
                              type="text"
                              name="workshop_group"
                              maxLength="50"
                              defaultValue={editingHireCategory?.workshop_group || ''}
                              {...createTooltipProps('workshop_group')}
                            />
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Inspection Test</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('inspection_test')}>
                                Default inspection test for this category
                              </Tooltip>
                            }
                          >
                            <Form.Select
                              name="inspection_test"
                              key={`insp-${editingHireCategory?.id || 'new'}-${inspectionTests.length}`}
                              defaultValue={editingHireCategory?.inspection_test || ''}
                              {...createTooltipProps('inspection_test')}
                            >
                              <option value="">Select inspection test...</option>
                              <MissingSelectOption
                                value={editingHireCategory?.inspection_test}
                                options={inspectionTests.map((testName) => ({ testName }))}
                                getOptionValue={(test) => test.testName}
                              />
                              {inspectionTests.map((testName, index) => (
                                <option key={index} value={testName}>
                                  {testName}
                                </option>
                              ))}
                            </Form.Select>
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Pre Delivery Inspection</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('pre_delivery_inspection')}>
                                Inspection required before equipment delivery
                              </Tooltip>
                            }
                          >
                            <Form.Control
                              type="text"
                              name="pre_delivery_inspection"
                              maxLength="50"
                              defaultValue={editingHireCategory?.pre_delivery_inspection || ''}
                              {...createTooltipProps('pre_delivery_inspection')}
                            />
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Container>
                </Tab>
                <Tab eventKey="nominals" title="Nominal Codes &amp; Options">
                  <Container fluid>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>N/C Hire</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('nc_hire')}>
                                Nominal code for hire revenue (Sales type)
                              </Tooltip>
                            }
                          >
                            <Form.Select
                              name="nc_hire"
                              key={`nch-${editingHireCategory?.id || 'new'}-${salesNominals.length}`}
                              defaultValue={editingHireCategory?.nc_hire || ''}
                              {...createTooltipProps('nc_hire')}
                            >
                              <option value="">Select nominal...</option>
                              <MissingSelectOption
                                value={editingHireCategory?.nc_hire}
                                options={salesNominals}
                                getOptionValue={(nc) => nc.code}
                              />
                              {salesNominals.map((nc) => (
                                <option key={nc.id} value={nc.code}>
                                  {nc.code} - {nc.name}
                                </option>
                              ))}
                            </Form.Select>
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>N/C Operated</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('nc_operated')}>
                                Nominal code for operated hire (Sales type)
                              </Tooltip>
                            }
                          >
                            <Form.Select
                              name="nc_operated"
                              key={`nco-${editingHireCategory?.id || 'new'}-${salesNominals.length}`}
                              defaultValue={editingHireCategory?.nc_operated || ''}
                              {...createTooltipProps('nc_operated')}
                            >
                              <option value="">Select nominal...</option>
                              <MissingSelectOption
                                value={editingHireCategory?.nc_operated}
                                options={salesNominals}
                                getOptionValue={(nc) => nc.code}
                              />
                              {salesNominals.map((nc) => (
                                <option key={nc.id} value={nc.code}>
                                  {nc.code} - {nc.name}
                                </option>
                              ))}
                            </Form.Select>
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>N/C Sales</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('nc_sales')}>
                                Nominal code for sales transactions (Sales type)
                              </Tooltip>
                            }
                          >
                            <Form.Select
                              name="nc_sales"
                              key={`ncs-${editingHireCategory?.id || 'new'}-${salesNominals.length}`}
                              defaultValue={editingHireCategory?.nc_sales || ''}
                              {...createTooltipProps('nc_sales')}
                            >
                              <option value="">Select nominal...</option>
                              <MissingSelectOption
                                value={editingHireCategory?.nc_sales}
                                options={salesNominals}
                                getOptionValue={(nc) => nc.code}
                              />
                              {salesNominals.map((nc) => (
                                <option key={nc.id} value={nc.code}>
                                  {nc.code} - {nc.name}
                                </option>
                              ))}
                            </Form.Select>
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>N/C Purchase</Form.Label>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip {...createTooltipProps('nc_purchase')}>
                                Nominal code for purchase transactions (Purchase type)
                              </Tooltip>
                            }
                          >
                            <Form.Select
                              name="nc_purchase"
                              key={`ncp-${editingHireCategory?.id || 'new'}-${purchaseNominals.length}`}
                              defaultValue={editingHireCategory?.nc_purchase || ''}
                              {...createTooltipProps('nc_purchase')}
                            >
                              <option value="">Select nominal...</option>
                              <MissingSelectOption
                                value={editingHireCategory?.nc_purchase}
                                options={purchaseNominals}
                                getOptionValue={(nc) => nc.code}
                              />
                              {purchaseNominals.map((nc) => (
                                <option key={nc.id} value={nc.code}>
                                  {nc.code} - {nc.name}
                                </option>
                              ))}
                            </Form.Select>
                          </OverlayTrigger>
                        </Form.Group>
                      </Col>
                    </Row>
                    <div className="mb-3 mt-2">
                      <h5 className="mb-3">Options</h5>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip {...createTooltipProps('req_serial_no')}>
                                  Requires serial number entry for equipment in this category
                                </Tooltip>
                              }
                            >
                              <Form.Check
                                type="checkbox"
                                name="req_serial_no"
                                label="Require Serial No"
                                defaultChecked={parseApiBool(editingHireCategory?.req_serial_no)}
                                {...createTooltipProps('req_serial_no')}
                              />
                            </OverlayTrigger>
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip {...createTooltipProps('warn_no_transport')}>
                                  Warns when no transport is available for this category
                                </Tooltip>
                              }
                            >
                              <Form.Check
                                type="checkbox"
                                name="warn_no_transport"
                                label="Warn No Transport"
                                defaultChecked={parseApiBool(
                                  editingHireCategory?.warn_no_transport
                                )}
                                {...createTooltipProps('warn_no_transport')}
                              />
                            </OverlayTrigger>
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip {...createTooltipProps('is_transport')}>
                                  Indicates this category represents transport equipment
                                </Tooltip>
                              }
                            >
                              <Form.Check
                                type="checkbox"
                                name="is_transport"
                                label="Is Transport"
                                defaultChecked={parseApiBool(editingHireCategory?.is_transport)}
                                {...createTooltipProps('is_transport')}
                              />
                            </OverlayTrigger>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip {...createTooltipProps('dont_suspend')}>
                                  Prevents suspension of hire contracts for this category
                                </Tooltip>
                              }
                            >
                              <Form.Check
                                type="checkbox"
                                name="dont_suspend"
                                label="Don't Suspend"
                                defaultChecked={parseApiBool(editingHireCategory?.dont_suspend)}
                                {...createTooltipProps('dont_suspend')}
                              />
                            </OverlayTrigger>
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip {...createTooltipProps('dont_discount')}>
                                  Prevents discounting of hire rates for this category
                                </Tooltip>
                              }
                            >
                              <Form.Check
                                type="checkbox"
                                name="dont_discount"
                                label="Don't Discount"
                                defaultChecked={parseApiBool(editingHireCategory?.dont_discount)}
                                {...createTooltipProps('dont_discount')}
                              />
                            </OverlayTrigger>
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip {...createTooltipProps('asset_only')}>
                                  Indicates this category is for asset management only
                                </Tooltip>
                              }
                            >
                              <Form.Check
                                type="checkbox"
                                name="asset_only"
                                label="Asset Only"
                                defaultChecked={parseApiBool(editingHireCategory?.asset_only)}
                                {...createTooltipProps('asset_only')}
                              />
                            </OverlayTrigger>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip {...createTooltipProps('meter_reading')}>
                                  Requires meter reading for equipment in this category
                                </Tooltip>
                              }
                            >
                              <Form.Check
                                type="checkbox"
                                name="meter_reading"
                                label="Meter Reading"
                                defaultChecked={parseApiBool(editingHireCategory?.meter_reading)}
                                {...createTooltipProps('meter_reading')}
                              />
                            </OverlayTrigger>
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </Container>
                </Tab>
              </Tabs>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="hireCategoryForm">
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
}

export default HireCategories;
